import { format } from 'date-fns';

import { DateFormat, ensureLocalDateFormat, getDatesFromTimeRange } from '@/lib/date';
import { Json, TimePrecision } from '@/lib/types';

import { getExplorationType, isModelExploration } from '@/core/exploration';

import {
  Exploration,
  ExplorationParameters,
  ModelKind,
  VariableDefinition,
  arrayParameter,
  booleanParameter,
  dateParameter,
  dateRangeParameter,
  numberParameter,
  stringParameter,
  timeIntervalParameter,
} from '../types';

export * from './exploration';
export * from './detail-exploration';
export * from './linking';
export * from './url';
export * from './validation';
export * from './variable';

export const isNumberType = (type: string | null | undefined) =>
  ['Number', 'Float', 'Integer'].includes(type ?? '');

export const isStringType = (type: string | null | undefined) =>
  ['String', 'Enum'].includes(type ?? '');

export const isDateType = (type: string | null | undefined) => type === 'Date';

const isUnparameterizedExploration = (exploration: Exploration) =>
  exploration.parameters.length === 0;

export const getUnparameterizedExplorations = (explorations: Exploration[]) =>
  explorations.filter(isUnparameterizedExploration);

export const isPersistedBaseExploration = (exploration: Exploration) =>
  isUnparameterizedExploration(exploration) && !isModelExploration(exploration);

export const parseParameterValue = (
  value: unknown,
  definition: VariableDefinition,
  timezone?: string,
) => {
  switch (definition.kind) {
    case 'string':
      return stringParameter.catch(() => definition.defaultValue).parse(value);
    case 'number':
      return numberParameter.catch(() => definition.defaultValue).parse(value);
    case 'enum':
      return stringParameter
        .or(numberParameter)
        .catch(() => definition.defaultValue)
        .parse(value);
    case 'multiselect':
      return arrayParameter.catch(() => definition.defaultValue).parse(value);
    case 'boolean':
      return booleanParameter.catch(() => definition.defaultValue).parse(value);
    case 'date':
      return ensureLocalDateFormat(
        dateParameter.catch(() => definition.defaultValue).parse(value),
        timezone,
      );
    case 'date_range': {
      const parsed = dateRangeParameter.catch(() => definition.defaultValue).parse(value);
      // Dynamic ranges: always recalculate start/end from NOW
      // This includes standard presets (e.g., '7d', 'prevweek') and dynamic shorthands (e.g., '21d', '3w')
      if (parsed.range !== 'custom') {
        const { startDate, endDate } = getDatesFromTimeRange(parsed.range, TimePrecision.Daily);
        // Use format() to preserve local date - toISOString() shifts dates due to UTC conversion
        const startStr = format(startDate, DateFormat);
        const endStr = format(endDate, DateFormat);
        return {
          range: parsed.range,
          start: `${startStr}T00:00:00.000Z`,
          end: `${endStr}T23:59:59.999Z`,
        };
      }
      // Custom range: start/end must be present
      if (parsed.start === undefined || parsed.end === undefined) {
        throw new Error('Custom date range requires start and end dates');
      }
      return { range: parsed.range, start: parsed.start, end: parsed.end };
    }
    case 'time_interval':
      return timeIntervalParameter.catch(() => definition.defaultValue).parse(value);
  }
};

export const getParameters = (
  urlParameters: Record<string, Json>,
  inputDefinitions: VariableDefinition[],
  timezone?: string,
): ExplorationParameters => {
  return inputDefinitions.reduce((acc, definition) => {
    return {
      ...acc,
      [definition.key]: parseParameterValue(urlParameters[definition.key], definition, timezone),
    };
  }, {});
};

export const getExplorationIconName = (exploration: Exploration) => {
  const type = getExplorationType(exploration);

  return type === 'model'
    ? 'Model'
    : type === 'ai'
      ? 'Zap'
      : type === 'exploration'
        ? 'Exploration'
        : 'Hash';
};

export const getModelKindIconName = (kind: ModelKind | null) => {
  switch (kind) {
    case 'Event':
      return 'Event';
    case 'User':
      return 'User';
    case 'Account':
      return 'Briefcase';
    default:
      return 'Box';
  }
};

export const getDataTypeFromValue = (value: Json) => {
  switch (true) {
    case typeof value === 'string':
      return 'String';
    case typeof value === 'number':
      return 'Number';
    case typeof value === 'boolean':
      return 'Boolean';
    case Array.isArray(value):
      return 'Array';
    case typeof value === 'object':
      return 'Object';
    default:
      return null;
  }
};
